import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
	ApiEvaluateSingleDriverResponse,
	ApiExportRosterCsvPayload,
	ApiExportRosterCsvResponse,
	ApiExportRosterXlsxPayload,
	ApiExportRosterXlsxResponse,
} from '@/rennovation/planning-view/types/api/roster-response';
import { useRosterApi } from '@/rennovation/common/services/api/useRosterApi';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';

export const useRosterStore = defineStore('roster', () => {
	// ============================================
	// STATE
	// ============================================
	const evaluationsById = ref<Record<string, ApiEvaluateSingleDriverResponse>>({});
	const isLoading = ref(false);
	const error = ref<string | null>(null);

	// ============================================
	// GETTERS
	// ============================================
	const allEvaluations = computed(() => Object.values(evaluationsById.value));
	const getEvaluationByDriverId = (driverId: string): ApiEvaluateSingleDriverResponse | undefined => evaluationsById.value[driverId];

	// ============================================
	// ACTIONS
	// ============================================
	const rosterApi = useRosterApi();
	const alertStore = useAlertStore();

	async function evaluateSingleDriver(driverId: string, from: string, to: string, debug = false): Promise<ApiEvaluateSingleDriverResponse | null> {
		isLoading.value = true;
		error.value = null;
		try {
			const result = await rosterApi.evaluateSingleDriver(driverId, from, to, debug);
			evaluationsById.value[driverId] = result;
			return result;
		} catch (err) {
			error.value = err.response?.data?.message ?? err.message;
			return null;
		} finally {
			isLoading.value = false;
		}
	}

	async function exportRosterCsv(payload: ApiExportRosterCsvPayload): Promise<ApiExportRosterCsvResponse | null> {
		isLoading.value = true;
		error.value = null;
		try {
			return await rosterApi.exportRosterCsv(payload);
		} catch (err) {
			error.value = err.response?.data?.message ?? err.message;
			alertStore.showAlert(error.value, 'error');
			return null;
		} finally {
			isLoading.value = false;
		}
	}

	async function exportRosterXlsx(payload: ApiExportRosterXlsxPayload): Promise<ApiExportRosterXlsxResponse | null> {
		isLoading.value = true;
		error.value = null;
		try {
			return await rosterApi.exportRosterXlsx(payload);
		} catch (err) {
			error.value = err.response?.data?.message ?? err.message;
			alertStore.showAlert(error.value, 'error');
			return null;
		} finally {
			isLoading.value = false;
		}
	}

	return {
		// state
		evaluationsById,
		isLoading,
		error,
		// getters
		allEvaluations,
		getEvaluationByDriverId,
		// actions
		evaluateSingleDriver,
		exportRosterCsv,
		exportRosterXlsx,
	};
});
